// AdminUserDetail — Full user detail view with all admin actions
// Tabbed: Overview, Trades, KYC, Sessions, Activity log, Notes

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TierPill } from '../../components/Brand';

export function AdminUserDetail() {
  const nav = useNavigate();
  const { id } = useParams();
  const [tab, setTab] = useState<'overview' | 'trades' | 'kyc' | 'sessions' | 'activity' | 'notes'>('overview');
  const [note, setNote] = useState('');

  // Mock data
  const user = {
    id: id || 'u1',
    name: 'Brandon Lee',
    email: 'demo@funderpro.live',
    tier: 'Professional' as const,
    status: 'active' as const,
    balance: 4250.00,
    pnl30d: 1840.50,
    pnlTotal: 12840.00,
    trades: 412,
    kyc: 'verified' as const,
    country: 'US',
    joined: '2026-07-04',
    lastActive: 'now',
    phone: '+1 (555) 123-4567',
    dob: '1992-03-15',
    address: '123 Main Street, San Francisco, CA 94102',
    ip: '203.0.113.42',
    device: 'iPhone 15 Pro · iOS 18.0',
  };

  return (
    <div className="space-y-4 max-w-5xl mx-auto">
      {/* Back */}
      <button onClick={() => nav('/admin/users')} data-testid="back" className="text-[12px] text-white/60 active:text-white">← Back to users</button>

      {/* Hero card */}
      <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-16 w-16 items-center justify-center rounded-[20px] bg-gradient-to-br from-[#007AFF] to-[#5856D6] text-white text-[24px] font-bold">BL</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-[20px] font-bold text-white">{user.name}</h1>
              <span className="rounded-full bg-[#34C759]/20 px-1.5 py-0.5 text-[9px] font-bold uppercase text-[#34C759]">{user.status}</span>
              <TierPill tier={user.tier} />
            </div>
            <div className="text-[12px] text-white/50">{user.email} · {user.id} · Joined {user.joined}</div>
            <div className="mt-2 text-[11px] text-white/40">Last active: {user.lastActive} · {user.country} · {user.ip}</div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
          <ActionBtn label="Send message" icon="💬" color="#FF9500" onClick={() => alert('Open message modal')} testid="act-message" />
          <ActionBtn label="Add balance"  icon="➕" color="#34C759" onClick={() => alert('Open balance modal')} testid="act-add" />
          <ActionBtn label="Deduct"       icon="➖" color="#FF3B30" onClick={() => alert('Open deduct modal')} testid="act-deduct" />
          <ActionBtn label="Ban account"  icon="🚫" color="#FF3B30" onClick={() => alert('Open ban modal')} testid="act-ban" />
        </div>
        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
          <ActionBtn label="Upgrade tier"  icon="⬆️" color="#007AFF" onClick={() => alert('Open tier modal')} testid="act-upgrade" />
          <ActionBtn label="Block IP"      icon="🌐" color="#FF3B30" onClick={() => alert('Open IP block')} testid="act-block-ip" />
          <ActionBtn label="Force logout"  icon="🚪" color="#FF9500" onClick={() => alert('Force logout all sessions')} testid="act-logout" />
          <ActionBtn label="Reset 2FA"     icon="🔐" color="#5856D6" onClick={() => alert('Reset 2FA')} testid="act-reset-2fa" />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Kpi label="Balance"    value={`$${user.balance.toFixed(0)}`} color="#34C759" />
        <Kpi label="30d P&L"    value={`+$${user.pnl30d.toFixed(0)}`} color="#34C759" />
        <Kpi label="Total P&L"  value={`+$${user.pnlTotal.toFixed(0)}`} color="#34C759" />
        <Kpi label="Trades"     value={user.trades.toString()} />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 overflow-x-auto border-b border-white/5" data-testid="user-tabs">
        {(['overview', 'trades', 'kyc', 'sessions', 'activity', 'notes'] as const).map((t) => (
          <button
            key={t} onClick={() => setTab(t)}
            data-testid={`tab-${t}`}
            className={['shrink-0 px-3 py-2 text-[12px] font-semibold capitalize border-b-2 transition',
              tab === t ? 'border-[#FF3B30] text-white' : 'border-transparent text-white/50 active:text-white'
            ].join(' ')}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'overview' && (
        <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 p-4">
          <h2 className="text-[14px] font-bold text-white mb-3">Personal info</h2>
          <div className="space-y-2">
            <InfoRow label="Full name" value={user.name} />
            <InfoRow label="Email"     value={user.email} />
            <InfoRow label="Phone"     value={user.phone} />
            <InfoRow label="DOB"       value={user.dob} />
            <InfoRow label="Address"   value={user.address} />
            <InfoRow label="Country"   value={`${user.country === 'US' ? 'United States' : user.country}`} />
            <InfoRow label="Tier"      value={user.tier} accent />
            <InfoRow label="KYC"       value={user.kyc} accent />
            <InfoRow label="Joined"    value={user.joined} />
            <InfoRow label="Last IP"   value={user.ip} />
            <InfoRow label="Device"    value={user.device} last />
          </div>
        </div>
      )}

      {tab === 'trades' && (
        <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-white/5 bg-white/[0.02] text-[10px] font-bold uppercase text-white/40">
            <div className="col-span-2">Date</div>
            <div className="col-span-2">Pair</div>
            <div className="col-span-1">Side</div>
            <div className="col-span-2 text-right">Entry</div>
            <div className="col-span-2 text-right">Exit</div>
            <div className="col-span-1 text-right">Qty</div>
            <div className="col-span-2 text-right">P&L</div>
          </div>
          {Array.from({ length: 10 }).map((_, i) => {
            const pnl = (Math.random() - 0.3) * 200;
            const isLong = i % 2 === 0;
            return (
              <div key={i} data-testid={`admin-trade-${i}`} className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-white/5 last:border-b-0 items-center text-[11px]">
                <div className="col-span-2 text-white/60">2026-07-{(20 - i).toString().padStart(2, '0')}</div>
                <div className="col-span-2 text-white font-semibold">ES</div>
                <div className="col-span-1"><span className={['rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase', isLong ? 'bg-[#34C759]/20 text-[#34C759]' : 'bg-[#FF3B30]/20 text-[#FF3B30]'].join(' ')}>{isLong ? 'L' : 'S'}</span></div>
                <div className="col-span-2 text-right text-white tabular-nums">5280.{(i * 12) % 100}</div>
                <div className="col-span-2 text-right text-white tabular-nums">5295.{(i * 17) % 100}</div>
                <div className="col-span-1 text-right text-white tabular-nums">{i % 3 + 1}</div>
                <div className={['col-span-2 text-right font-bold tabular-nums', pnl >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'].join(' ')}>{pnl >= 0 ? '+' : ''}${Math.abs(pnl).toFixed(2)}</div>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'kyc' && (
        <div className="space-y-3">
          <div className="rounded-[16px] bg-[#1C1C1E] border border-[#34C759]/30 p-4">
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#34C759]/20 text-[20px]">✓</div>
              <div>
                <div className="text-[14px] font-bold text-white">KYC verified</div>
                <div className="text-[11px] text-white/50">Approved 2026-07-04 by admin@funderpro.live</div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-[10px] bg-white/5 p-2"><div className="text-[10px] uppercase text-white/40">Doc type</div><div className="text-[12px] font-semibold text-white">Passport</div></div>
              <div className="rounded-[10px] bg-white/5 p-2"><div className="text-[10px] uppercase text-white/40">Liveness</div><div className="text-[12px] font-semibold text-[#34C759]">Passed</div></div>
              <div className="rounded-[10px] bg-white/5 p-2"><div className="text-[10px] uppercase text-white/40">Score</div><div className="text-[12px] font-semibold text-white">98/100</div></div>
            </div>
            <button onClick={() => alert('View documents')} data-testid="view-kyc-docs" className="mt-3 flex w-full h-10 items-center justify-center rounded-[8px] bg-white/5 text-[12px] font-semibold text-white/80 active:bg-white/10">View submitted documents</button>
          </div>
        </div>
      )}

      {tab === 'sessions' && (
        <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 p-4 space-y-2">
          {[
            { device: 'iPhone 15 Pro · iOS 18.0',  ip: '203.0.113.42',  loc: 'San Francisco, US', current: true,  time: 'now' },
            { device: 'MacBook Pro · Safari 17',      ip: '203.0.113.42',  loc: 'San Francisco, US', current: false, time: '2h ago' },
            { device: 'iPad Air · iPadOS 18',          ip: '198.51.100.7', loc: 'San Francisco, US', current: false, time: '1d ago' },
          ].map((s, i) => (
            <div key={i} data-testid={`session-${i}`} className="flex items-center gap-3 border border-white/5 rounded-[10px] p-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-white/5 text-[16px]">{s.device.includes('iPhone') ? '📱' : s.device.includes('Mac') ? '💻' : '📟'}</div>
              <div className="flex-1">
                <div className="text-[12px] font-semibold text-white">{s.device} {s.current && <span className="rounded-full bg-[#34C759] px-1.5 py-0.5 text-[8px] font-bold uppercase text-white ml-1">Current</span>}</div>
                <div className="text-[10px] text-white/50">{s.ip} · {s.loc} · {s.time}</div>
              </div>
              {!s.current && <button onClick={() => alert('Terminated')} data-testid={`terminate-${i}`} className="rounded-full bg-[#FF3B30]/20 px-3 py-1 text-[10px] font-semibold text-[#FF3B30]">Terminate</button>}
            </div>
          ))}
        </div>
      )}

      {tab === 'activity' && (
        <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 p-4">
          <div className="space-y-1.5">
            {Array.from({ length: 15 }).map((_, i) => {
              const events = [
                { t: 'Bot trade closed', d: 'NQ +$95' },
                { t: 'Login',            d: 'iPhone 15 Pro' },
                { t: 'KYC submitted',    d: 'Passport + selfie' },
                { t: 'Challenge started', d: '$50K Beginner' },
                { t: 'Payout requested', d: '$1,240 ACH' },
                { t: 'Bot subscribed',   d: 'Gold Digger' },
              ];
              const e = events[i % events.length];
              return (
                <div key={i} data-testid={`act-${i}`} className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-b-0">
                  <div className="text-[10px] text-white/40 tabular-nums w-20 shrink-0">2026-07-{String(24 - i).padStart(2, '0')} 11:3{i}</div>
                  <div className="flex-1 text-[12px] text-white">{e.t}</div>
                  <div className="text-[10px] text-white/50">{e.d}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'notes' && (
        <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 p-4">
          <h2 className="text-[14px] font-bold text-white mb-3">Admin notes (audit log)</h2>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add internal note about this user..." data-testid="admin-note" rows={3} className="w-full rounded-[10px] bg-black/40 border border-white/10 p-3 text-[13px] text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF3B30] resize-none" />
          <button onClick={() => { alert('Note saved'); setNote(''); }} data-testid="save-note" className="mt-2 h-10 px-4 rounded-[8px] bg-white/5 text-[12px] font-semibold text-white/80 active:bg-white/10">Save note</button>
          <div className="mt-4 space-y-2">
            {[
              { admin: 'admin@funderpro.live', time: '2026-07-15 14:32', text: 'Reviewed P&L report, looks normal. Approving for Pro tier upgrade.' },
              { admin: 'support@funderpro.live', time: '2026-07-08 10:15', text: 'User requested help with API. Replied with docs link.' },
            ].map((n, i) => (
              <div key={i} data-testid={`note-${i}`} className="rounded-[10px] bg-white/5 p-3">
                <div className="flex items-center gap-2">
                  <div className="text-[12px] font-semibold text-white">{n.admin}</div>
                  <div className="text-[10px] text-white/40">{n.time}</div>
                </div>
                <p className="mt-1 text-[12px] text-white/80">{n.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ActionBtn({ label, icon, color, onClick, testid }: { label: string; icon: string; color: string; onClick: () => void; testid?: string }) {
  return (
    <button onClick={onClick} data-testid={testid} className="flex h-10 items-center justify-center gap-1.5 rounded-[10px] text-[12px] font-semibold text-white active:opacity-40" style={{ backgroundColor: color + '20', color }}>
      <span>{icon}</span>
      {label}
    </button>
  );
}

function Kpi({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="rounded-[12px] bg-[#1C1C1E] border border-white/5 p-3 text-center">
      <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
      <div className="mt-1 text-[18px] font-bold tabular-nums" style={{ color: color || '#FFFFFF' }}>{value}</div>
    </div>
  );
}

function InfoRow({ label, value, accent, last }: { label: string; value: string; accent?: boolean; last?: boolean }) {
  return (
    <div className={['flex items-center justify-between py-2', last ? '' : 'border-b border-white/5'].join(' ')}>
      <span className="text-[12px] text-white/50">{label}</span>
      <span className={['text-[13px] font-medium', accent ? 'text-[#FF9500]' : 'text-white'].join(' ')}>{value}</span>
    </div>
  );
}
